<template>
    <div class="is-user-menu" :class="{ hasNoBreakOnMobile }" v-bind="$attrs">
        <e-dropdown
            ref="dropdown"
            :class="{
                'is-width-full': hasNoBreakOnMobile && ($isMobile || $isTablet),
            }"
            :disabled="!showDropdown"
            :append-to-body="appendToBody"
            @active-change="menuTriggered"
        >
            <template #trigger="{ active }">
                <div
                    :class="{
                        active,
                        'is-mobile': !hasNoBreakOnMobile,
                        'is-width-full': hasNoBreakOnMobile && ($isMobile || $isTablet),
                        'has-text-centered': hasNoBreakOnMobile && $isMobile,
                        'is-clickable': showDropdown,
                    }"
                    class="media tw:items-center! is-clickable"
                >
                    <slot v-if="showImage" :user="user" name="user-photo">
                        <div class="media-left">
                            <user-avatar
                                :img-url="user.photo_url"
                                :title="user.name"
                                :user="user"
                                :size="imageDimensions"
                                :skeleton="avatarSkeleton"
                                class="user-menu-avatar"
                            />
                        </div>
                    </slot>

                    <div v-if="showName" class="media-content">
                        <div>
                            <slot :user="user" name="user-name">
                                <span :style="{ fontWeight: fontWeight }">{{ user.name }}</span>
                            </slot>
                            <slot name="subtext" />
                        </div>
                        <span v-if="showExternalId" class="has-text-subtle">{{ user.external_id }}</span>
                    </div>

                    <div v-if="showDropdown" class="user-menu-chevron">
                        <slot :user="user" name="dropdown">
                            <e-icon :icon="`angle-${active ? 'up' : 'down'} is-adjusted-chevron`" />
                        </slot>
                    </div>
                </div>
            </template>

            <e-dropdown-item v-if="$route.name !== 'person-view' && !hideContactMenuItems" has-link>
                <router-link :to="profile">
                    <e-icon icon="user" size="is-small" />
                    View Profile
                </router-link>
            </e-dropdown-item>

            <e-dropdown-item v-if="!hideContactMenuItems" has-link>
                <a href="#" @click.prevent="openNewConversation(user.id)" @keyup.enter="openNewConversation(user.id)">
                    <e-icon icon="comment" size="is-small" />
                    Direct Message
                </a>
            </e-dropdown-item>

            <e-dropdown-item
                v-if="$gate.allows('viewEmail', ['User', user]) && !hideContactMenuItems && user.email"
                has-link
            >
                <a :href="`mailto: ${user.email}`">
                    <e-icon icon="envelope-alt" size="is-small" />
                    Email
                </a>
            </e-dropdown-item>

            <slot name="additional-actions" />
        </e-dropdown>
    </div>
</template>

<script>
import UserAvatar from '@/components/elements/Avatar.vue';

export default {
    name: 'UserMenu',

    components: { UserAvatar },

    props: {
        user: {
            required: true,
            type: Object,
        },

        showImage: {
            type: Boolean,
            required: false,
            default: true,
        },

        showName: {
            type: Boolean,
            required: false,
            default: true,
        },

        showExternalId: {
            type: Boolean,
            required: false,
            default: false,
        },

        showDropdown: {
            type: Boolean,
            required: false,
            default: true,
        },

        hideContactMenuItems: {
            type: Boolean,
            required: false,
            default: false,
        },

        imageSize: {
            type: [String, Number],
            required: false,
            default: 48,
        },

        hasNoBreakOnMobile: {
            type: Boolean,
            default: false,
        },

        hideBorder: {
            type: Boolean,
            default: false,
        },

        showStatuses: {
            type: Boolean,
            default: false,
        },

        appendToBody: {
            type: Boolean,
            default: false,
        },

        avatarSkeleton: {
            type: Boolean,
            default: false,
        },

        fontWeight: {
            type: Number,
            required: false,
            default: 300,
        },
    },

    data: () => ({
        loadingConversation: false,
    }),

    computed: {
        profile() {
            return { name: 'person-view', params: { personId: this.user.id } };
        },

        imageDimensions() {
            return typeof this.imageSize === 'string' && this.imageSize.includes('x')
                ? this.imageSize
                : `${this.imageSize}x${this.imageSize}`;
        },
    },

    methods: {
        menuTriggered(e) {
            if (e && !this.showDropdown) this.$refs.dropdown.toggle();
        },

        async openNewConversation(id) {
            if (!id) return;

            this.loadingConversation = true;

            await this.$store.dispatch('Conversation/openNewConversation', id);

            this.loadingConversation = false;
        },
    },
};
</script>

<style lang="scss">
@import 'resources/scss/toolbox.scss';

.is-user-menu {
    &.hasNoBreakOnMobile {
        .media {
            @include until($tablet) {
                display: block;
            }

            &-left,
            &-content {
                @include until($tablet) {
                    margin-bottom: $column-gap;
                    text-align: center;
                }
            }

            &-left {
                @include until($tablet) {
                    margin-right: 0;
                }

                figure {
                    @include until($tablet) {
                        margin: 0 auto;
                    }
                }
            }

            &-right {
                @include until($tablet) {
                    margin-left: 0;
                }
            }
        }

        .dropdown-trigger {
            @include until($tablet) {
                width: 100%;
            }
        }
    }

    .media {

        &-left,
        &-content,
        &-right {
            @include until($tablet) {
                width: auto;
            }
        }

        &-left {
            @include until($tablet) {
                margin-right: $column-gap;
            }
        }

        &-right {
            @include until($tablet) {
                margin-left: $column-gap;
            }
        }
    }
}
</style>
